import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalOneOf, searchPatch, optionalSearch } from "@/lib/search";
import { SearchField } from "@/components/app/patterns/search-field";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { relationshipLabel, errorWords } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RowActions } from "@/components/app/patterns/row-actions";
import { RecordCard } from "@/components/app/lists/record-card";
import { EmptyRows } from "@/components/app/lists/empty-rows";
import { RecordRow } from "@/components/app/lists/record-row";
import { SharedRowNote } from "@/components/app/lists/shared-row-note";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Badge } from "@/components/ui/badge";
import { useNewRowPlace } from "@/hooks/use-new-row-place";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan, useCanChangeRow } from "@/lib/policy";
import { ChecklistTemplateActions } from "./-components/checklists/checklist-template-actions";
import { ChecklistBulkAssign } from "./-components/checklists/checklist-bulk-assign";
import {
  ChecklistItemDialog,
  NEW_CHECKLIST_ITEM,
  type ChecklistItemDraft,
} from "./-components/checklists/checklist-item-dialog";
import { ChecklistOverview } from "./-components/checklists/checklist-overview";
import { ChecklistTemplateDialog } from "./-components/checklists/checklist-template-dialog";
import { ChecklistTemplateItems } from "./-components/checklists/checklist-template-items";
import { reachLabel } from "./-components/messaging/facility-reach-field";
import { NoMatches } from "@/routes/_authenticated/_administration/settings/-components/no-matches";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import { guardRoute } from "@/lib/policy/route-guard";

export const Route = createFileRoute("/_authenticated/_administration/settings/checklists")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; status?: "pending" | "completed" | "waived" } => ({
    q: optionalSearch(search.q),
    status: optionalOneOf(["pending", "completed", "waived"], search.status),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "checklists"),
  component: ChecklistSettings,
});

const fail = (error: Error) => toast.error(errorWords(error));
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;
/** A row in every building answers organization-wide; otherwise at its home building. */
const rowAt = (row: { appliesToAllFacilities: boolean; facilityId: string | null }) =>
  row.appliesToAllFacilities ? null : row.facilityId;

/** The things that have to happen, and the sets a move-in applies in one go. */
function ChecklistSettings() {
  const { facilityId } = useFacility();
  /* Giving residents a checklist is care work: the API asks resident.profile:update there. */
  const canAssign = useCan("resident.profile:update");
  // The catalog is kept where settings are managed, at each row's own building, and a row in
  // every building only organization-wide (the API's curation rule).
  const canChange = useCanChangeRow("settings:manage");
  const place = useNewRowPlace("settings:manage");
  const curates = place.everyBuilding || place.buildings.length > 0;
  const changeable = (row: Parameters<typeof rowAt>[0]) => canChange(rowAt(row));
  const queryClient = useQueryClient();
  const [editingItem, setEditingItem] = useState<ChecklistItemDraft | null>(null);
  const [removingItem, setRemovingItem] = useState<{ id: string; title: string } | null>(null);
  const [addingTemplate, setAddingTemplate] = useState(false);
  const { q, status } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const { text: search, change: setSearch } = useSearchParamText(q, (next) => patch({ q: next }));
  const [openTemplate, setOpenTemplate] = useState<{
    id: string;
    name: string;
    changeable: boolean;
  } | null>(null);

  const items = useQuery(
    orpc.care.checklists.items.list.queryOptions({ input: { facilityId, includeInactive: true } }),
  );
  const templates = useQuery(
    orpc.care.checklists.templates.list.queryOptions({ input: { facilityId } }),
  );
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.care.checklists.key() });

  const updateItem = useMutation(
    orpc.care.checklists.items.update.mutationOptions({ onSuccess: invalidate, onError: fail }),
  );
  const removeItem = useMutation(
    orpc.care.checklists.items.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Checklist item deleted");
        setRemovingItem(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const where = (everywhere: boolean, ids: readonly string[]) =>
    reachLabel(everywhere, ids, facilities.data ?? []);

  const needle = search.trim().toLowerCase();
  const itemRows = (items.data ?? []).filter(
    (item) =>
      !needle ||
      item.title.toLowerCase().includes(needle) ||
      (item.description ?? "").toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        title="Checklists"
        meta="The steps that have to happen for a resident, like a move-in, and the templates that hand them out in one go."
      />

      <SearchField value={search} onChange={setSearch} placeholder="Search checklist items" />

      <RecordCard
        title="Checklist items"
        count={items.data ? itemRows.length : undefined}
        canWrite={curates}
        addLabel="Add checklist item"
        onAdd={() =>
          setEditingItem({
            ...NEW_CHECKLIST_ITEM,
            facilityIds: place.startAt ? [place.startAt] : [],
          })
        }
      >
        {items.isPending ? (
          <RowsSkeleton />
        ) : items.isError ? (
          <ReadFailed
            message="The checklist items could not be loaded."
            error={items.error}
            retrying={items.isFetching}
            onRetry={() => void items.refetch()}
          />
        ) : itemRows.length === 0 ? (
          needle ? (
            <NoMatches search={search} onClear={() => setSearch("")} />
          ) : (
            <EmptyRows text="No checklist items yet. Add one for each thing that has to happen, like Copy of the power of attorney." />
          )
        ) : (
          <ul className="divide-y">
            {itemRows.map((item) => (
              <RecordRow
                key={item.id}
                title={
                  <span className="flex flex-wrap items-center gap-2">
                    {item.title}
                    {item.category ? (
                      <Badge variant="outline">{relationshipLabel(item.category)}</Badge>
                    ) : null}
                    {item.isRequired ? null : <Badge variant="secondary">Optional</Badge>}
                    {item.isActive ? null : <Badge variant="secondary">Off</Badge>}
                  </span>
                }
                meta={where(item.appliesToAllFacilities, item.facilityIds)}
                actions={
                  changeable(item) ? (
                    <RowActions
                      modal={false}
                      label={item.title}
                      primary={{
                        label: "Edit",
                        onSelect: () =>
                          setEditingItem({
                            id: item.id,
                            title: item.title,
                            description: item.description ?? "",
                            category: item.category,
                            isRequired: item.isRequired,
                            appliesToAllFacilities: item.appliesToAllFacilities,
                            facilityIds: item.facilityIds,
                          }),
                      }}
                      items={[
                        {
                          label: item.isActive ? "Switch item off" : "Switch item on",
                          disabled: updateItem.isPending,
                          onSelect: () =>
                            updateItem.mutate({ itemId: item.id, isActive: !item.isActive }),
                        },
                        {
                          label: "Delete checklist item",
                          destructive: true,
                          onSelect: () => setRemovingItem({ id: item.id, title: item.title }),
                        },
                      ]}
                    />
                  ) : curates && rowAt(item) === null ? (
                    <SharedRowNote />
                  ) : null
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      <RecordCard
        title="Templates"
        count={templates.data?.length}
        canWrite={curates}
        addLabel="Add template"
        onAdd={() => setAddingTemplate(true)}
      >
        {templates.isPending ? (
          <RowsSkeleton />
        ) : templates.isError ? (
          <ReadFailed
            message="The checklist templates could not be loaded."
            error={templates.error}
            retrying={templates.isFetching}
            onRetry={() => void templates.refetch()}
          />
        ) : !templates.data?.length ? (
          <EmptyRows text="No templates yet. Add one to give a resident a whole set of items at once, like Move-in." />
        ) : (
          <ul className="divide-y">
            {templates.data.map((row) => (
              <RecordRow
                key={row.id}
                title={row.name}
                meta={[
                  plural(row.itemCount, "item"),
                  where(row.appliesToAllFacilities, row.facilityIds),
                  row.description,
                  row.isActive ? null : "Off",
                ]
                  .filter(Boolean)
                  .join(", ")}
                actions={
                  /* The template itself was fixed from the moment it was
                     created: a typo could only be worked around by building
                     a second and leaving the first switched on. */
                  <ChecklistTemplateActions
                    template={row}
                    canManage={changeable(row)}
                    primary={{
                      label: "Show items",
                      onSelect: () =>
                        setOpenTemplate({
                          id: row.id,
                          name: row.name,
                          changeable: changeable(row),
                        }),
                    }}
                  />
                }
              />
            ))}
          </ul>
        )}
      </RecordCard>

      {canAssign && facilityId !== "all" ? (
        <ChecklistBulkAssign
          key={facilityId}
          facilityId={facilityId}
          templates={templates.data ?? []}
        />
      ) : null}

      <ChecklistOverview
        facilityId={facilityId}
        status={status ?? "all"}
        onStatus={(next) =>
          patch({ status: optionalOneOf(["pending", "completed", "waived"], next) })
        }
      />

      {editingItem ? (
        <ChecklistItemDialog
          key={editingItem.id ?? "new"}
          draft={editingItem}
          facilities={place.buildings}
          everywhereAllowed={place.everyBuilding}
          onClose={() => setEditingItem(null)}
          onSaved={invalidate}
        />
      ) : null}

      {removingItem ? (
        <RemoveConfirm
          title={`Delete “${removingItem.title}”?`}
          confirmLabel="Delete checklist item"
          description="Every assignment of it goes too, including the ones already ticked, so the record of who did it is lost. Switching it off keeps all of that and takes it off new checklists."
          onConfirm={() => removeItem.mutate({ itemId: removingItem.id })}
          onClose={() => setRemovingItem(null)}
        />
      ) : null}
      {addingTemplate ? (
        <ChecklistTemplateDialog
          open={addingTemplate}
          facilities={place.buildings}
          everywhereAllowed={place.everyBuilding}
          startAt={place.startAt}
          onOpenChange={setAddingTemplate}
          onSaved={invalidate}
        />
      ) : null}

      {openTemplate ? (
        <ChecklistTemplateItems
          templateId={openTemplate.id}
          templateName={openTemplate.name}
          items={items.data ?? []}
          canManage={openTemplate.changeable}
          onClose={() => setOpenTemplate(null)}
        />
      ) : null}
    </>
  );
}
